import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import amplitude from 'amplitude-js';
import { toggleSpinner, toggleSnackBar } from 'apps/promo/actions/appAction';
import { toggleChat } from 'apps/promo/actions/chatAction';
import { CommonPagePromo } from 'apps/promo/components/common/CommonPagePromo';
import { LightButton } from 'apps/promo/components/common/fromAuth/LightButton/LightButton';
import { LightDangerouslyHtml } from 'apps/promo/components/common/fromAuth/LightDangerouslyHtml/LightDangerouslyHtml';
import { LightDiskButton } from 'apps/promo/components/common/fromAuth/LightDiskButton/LightDiskButton';
import { LightHeaderPanel } from 'apps/promo/components/common/fromAuth/LightHeaderPanel/LightHeaderPanel';
import { LightText } from 'apps/promo/components/common/fromAuth/LightText/LightText';
import { MobileTextComponent, VoteBlock } from 'apps/promo/components/help/styles';
import NotAuthBasePage from 'apps/promo/components/NotAuthBasePage';
import { getHelpCenterUrl, getZendeskLocale, useGetQueryParams } from 'apps/promo/components/utils';
import { GET_CHAT_CLICKED } from 'apps/promo/constants/amplitude/AmplitudeEvents';
import { ARTICLE, SECTION } from 'apps/promo/constants/amplitude/AmplitudeParamName';
import { getLangCountryPrefix } from 'apps/promo/helpers/utils';

const ZendeskHelpMobileArticlePage = (props) => {
    const [voteVal, setVoteVal] = useState(0);

    const article = props.article;
    const url = `${getLangCountryPrefix(props.lang, props.country)}/help-mobile`;
    const location = useLocation();
    const urlParams = useGetQueryParams();
    const isMobilePlatform = urlParams.platform === 'ios' || urlParams.platform === 'android';
    const isAnonymousChat = urlParams.anonymousChat === 'true';
    const userEmail = urlParams.email;
    const token = urlParams.token;
    const version = urlParams.version;
    const voteInfo = props.voteInfo;
    const isEndArticle = 'endArticle' in urlParams;

    useEffect(() => {
        if (voteVal === 0) {
            setVoteVal(voteInfo?.value);
        }
    }, [voteVal]);

    const chatLink = () => {
        if (location.pathname.includes('showChat')) {
            return getHelpCenterUrl(`${location.pathname}`, urlParams);
        }
        return getHelpCenterUrl(`${location.pathname}/showChat`, urlParams);
    };

    const handleToggleChat = () => {
        if (!urlParams.platform) {
            props.toggleChat(true);
        }
    };

    const hasDirectLinkLabel = article?.label_names?.includes('directLink');
    const hasPopularLabel = article?.label_names?.includes(urlParams.popularLabel);

    const vote = async (isUpVote) => {
        if (isUpVote) {
            setVoteVal(1);
        } else if (!isUpVote) {
            setVoteVal(-1);
        }
        const response = await fetch('/api/zendesk/vote', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                articleId: article.id,
                userEmail: userEmail,
                token: token,
                locale: getZendeskLocale(props.lang, props.country),
                isUpVote: isUpVote,
            }),
        });
        props.toggleSnackBar('true', props.snackbarMessage);
        return response.ok;
    };

    const canShowVoteBlock = (voteInfo) => {
        if (isMobilePlatform && version !== 'c3p0') {
            return userEmail;
        } else if (token && version === 'c3p0') {
            return true;
        }
        return voteInfo && voteInfo.authorized;
    };

    const canShowChatBlock = (voteInfo, voteVal, isAnonymousChat) => {
        if (isAnonymousChat) {
            return true;
        } else if ((isMobilePlatform && userEmail) || (token && version === 'c3p0')) {
            return voteVal === -1; //кейс с новым приложением (показываем чат - если клиент 👎)
        } else if (isMobilePlatform && version !== 'c3p0') {
            return true; //кейс со старым приложением (не поддерживает голосование - всегда показываем чат)
        }
        return voteVal === -1; //кейс web (показываем чат - если есть данные о голосовании)
    };

    return (
        <CommonPagePromo
            useThemeColor
            backUrl={
                hasDirectLinkLabel || isEndArticle
                    ? null
                    : hasPopularLabel
                      ? getHelpCenterUrl(url, urlParams)
                      : getHelpCenterUrl(`${url}/category/${props.category.id}/section/${props.article.section_id}`, urlParams)
            }
            type={'tagA'}
            rightHeaderComponent={
                urlParams.platform || isEndArticle ? null : (
                    <LightDiskButton
                        type={'tagA'}
                        href={props.backUrl}
                        onClick={() => {
                            props.toggleSpinner(true);
                            sessionStorage?.removeItem('prevUrl');
                        }}
                        iconName={'cross'}
                    />
                )
            }
        >
            <LightHeaderPanel useThemeColor title={article?.title} />
            <MobileTextComponent>
                <LightDangerouslyHtml dangerouslyHtml={article?.body} />
            </MobileTextComponent>
            {canShowVoteBlock(voteInfo) && (
                <VoteBlock>
                    <LightText color="secondary">{props.opinionText}</LightText>
                    <div>
                        <LightDiskButton
                            iconName={'thumbs-up'}
                            iconColor={voteVal === 1 ? 'white' : 'primary'}
                            type={'button'}
                            variant={voteVal === 1 ? 'primary' : 'secondary'}
                            raised={false}
                            onClick={async () => await vote(true)}
                        />
                        <LightDiskButton
                            iconName={'thumbs-down'}
                            iconColor={voteVal === -1 ? 'white' : 'primary'}
                            type={'button'}
                            variant={voteVal === -1 ? 'primary' : 'secondary'}
                            raised={false}
                            onClick={async () => await vote(false)}
                        />
                    </div>
                </VoteBlock>
            )}
            {canShowChatBlock(voteInfo, voteVal, isAnonymousChat) && (
                <LightButton
                    type={urlParams.platform ? 'link' : null}
                    href={urlParams.platform ? chatLink() : null}
                    caption={props.chatButtonCaption}
                    variant={'row'}
                    textCenter
                    negativeMargin
                    onClick={() => {
                        if (!isMobilePlatform) {
                            amplitude.getInstance().logEvent(GET_CHAT_CLICKED, { [ARTICLE]: article.id, [SECTION]: article.section_id });
                        }
                        handleToggleChat();
                    }}
                />
            )}
        </CommonPagePromo>
    );
};

const mapStateToProps = ({ extra }) => ({
    chatButtonCaption: extra?.strapiApi?.chat_btn_mobile,
    opinionText: extra?.strapiApi?.opinion_text,
    category: extra?.zendeskHelpData?.category,
    sections: extra?.zendeskHelpData?.sections,
    article: extra?.zendeskHelpData?.article,
    voteInfo: extra?.zendeskHelpData?.voteInfo,
    lang: extra.noAuthFooter?.language?.lang,
    country: extra.noAuthFooter?.language?.country,
    snackbarMessage: extra.noAuthFooter?.messages?.snackbarMessage,
});

export default connect(mapStateToProps, { toggleChat, toggleSpinner, toggleSnackBar })(NotAuthBasePage(ZendeskHelpMobileArticlePage));
